import { rootInstance } from './axiosConfig';

const expiredListeners = new Set<() => void>();
let refreshPending: Promise<void> | null = null;
let sessionExpired = false;
let generation = 0;
const RENEWAL_KEY = 'comarca_session_renewed_at';
const recentRenewal = () => {
    try { const age = Date.now() - Number(window.localStorage.getItem(RENEWAL_KEY) || 0); return age >= 0 && age < 5000; }
    catch { return false; }
};
const recordRenewal = () => {
    // Only coordination metadata, never credentials or user information.
    try { window.localStorage.setItem(RENEWAL_KEY, String(Date.now())); } catch { /* Storage may be disabled. */ }
};

export const onSessionExpired = (callback: () => void) => {
    expiredListeners.add(callback);
    if (sessionExpired) callback();
    return () => { expiredListeners.delete(callback); };
};
export const markSessionActive = () => { generation += 1; sessionExpired = false; };
export const handleSessionExpired = () => {
    if (sessionExpired) return;
    sessionExpired = true;
    expiredListeners.forEach(callback => callback());
};

const renew = async (startedGeneration: number): Promise<void> => {
    // A tab ahead of us may already have rotated the shared HttpOnly cookies.
    if (recentRenewal()) return;
    try { await rootInstance.post('refresh/'); recordRenewal(); }
    catch (error: any) {
        if (error.response?.status !== 401) throw error;
        // Browsers without Web Locks can race. Confirm invalid authentication
        // using the CURRENT cookies before interpreting a single-use refresh
        // rejection as logout. Network/403/5xx never establish expiration.
        for (const delay of [0, 150, 350]) {
            if (delay) await new Promise(resolve => window.setTimeout(resolve, delay));
            try { await rootInstance.get('/api/me'); recordRenewal(); return; }
            catch (proofError: any) {
                if (proofError.response?.status !== 401) throw proofError;
            }
        }
        if (startedGeneration === generation) handleSessionExpired();
        throw error;
    }
};

export const refreshAccessToken = (): Promise<void> => {
    if (sessionExpired) return Promise.reject(new Error('La sesión finalizó. Inicia sesión nuevamente.'));
    if (!refreshPending) {
        const startedGeneration = generation;
        // Web Locks coordinate all tabs in this origin; the in-memory promise
        // still deduplicates requests within one tab. Server rotation stays on.
        const locks = navigator.locks;
        refreshPending = (locks
            ? locks.request('comarca-session-refresh', () => renew(startedGeneration))
            : renew(startedGeneration)).then(() => undefined).finally(() => { refreshPending = null; });
    }
    return refreshPending;
};

export const handleRequestWithAuth = async <T>(requestFunc: () => Promise<T>): Promise<T> => {
    try { return await requestFunc(); }
    catch (error: any) {
        if (error.response?.status !== 401) throw error;
        await refreshAccessToken();
        try { return await requestFunc(); }
        catch (retryError: any) {
            if (retryError.response?.status === 401) {
                // A fresh identity check distinguishes authorization/account
                // revocation from a request that raced another tab's cookies.
                try { await rootInstance.get('/api/me'); }
                catch (proofError: any) {
                    if (proofError.response?.status === 401) handleSessionExpired();
                    throw proofError;
                }
            }
            throw retryError;
        }
    }
};
